import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MapPin, Phone, Linkedin, Mail, Loader2 } from "lucide-react";
import { getSettings, submitContact } from "@/lib/api";
import { Seo } from "@/components/site/Seo";
import { PageIntro } from "@/components/site/PageIntro";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATEGORIES = ["General", "Business Partnerships", "Media", "Careers", "Investment Enquiries"];

export default function Contact() {
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: getSettings });
  const [form, setForm] = useState({ category: "General", name: "", email: "", organisation: "", message: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.email.trim()) e.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email.";
    if (!form.message.trim()) e.message = "Please enter a message.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await submitContact(form);
      toast.success(res.message || "Your enquiry has been received.");
      setForm({ category: "General", name: "", email: "", organisation: "", message: "" });
      setErrors({});
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = { background: "var(--surface)", borderColor: "var(--card-border)", color: "var(--text)" };
  const addr = settings?.address || {};

  return (
    <>
      <Seo title="Contact" path="/contact" settings={settings} description="Contact Acharya Holdings for general enquiries, partnerships, media, careers, and investment." />
      <PageIntro eyebrow="Contact" title="Start a conversation."
        description="For partnerships, media, careers, or investment enquiries, please reach out using the details below or the form." />

      <div className="surface-cream">
      <section className="container-inst py-16 lg:py-24">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1fr_1.1fr] lg:gap-16">
          {/* details */}
          <div className="space-y-8">
            <div className="flex gap-4">
              <MapPin size={20} style={{ color: "var(--accent)" }} className="mt-0.5 shrink-0" />
              <address className="space-y-0.5 text-sm not-italic" style={{ color: "var(--muted)" }}>
                <p className="font-medium" style={{ color: "var(--text)" }}>{settings?.companyName || "Acharya Holdings"}</p>
                {addr.line1 && <p>{addr.line1}</p>}
                {addr.line2 && <p>{addr.line2}</p>}
                {addr.line3 && <p>{addr.line3}</p>}
                {addr.line4 && <p>{addr.line4}</p>}
              </address>
            </div>
            <div className="flex gap-4">
              <Phone size={20} style={{ color: "var(--accent)" }} className="mt-0.5 shrink-0" />
              <div className="space-y-0.5 text-sm" style={{ color: "var(--muted)" }}>
                {(settings?.phones || []).map((p) => (<p key={p}><a href={`tel:${p.replace(/\s/g,"")}`}>{p}</a></p>))}
              </div>
            </div>
            {settings?.email && (
              <div className="flex gap-4">
                <Mail size={20} style={{ color: "var(--accent)" }} className="mt-0.5 shrink-0" />
                <a href={`mailto:${settings.email}`} className="text-sm" style={{ color: "var(--muted)" }}>{settings.email}</a>
              </div>
            )}
            {settings?.social?.linkedin && (
              <div className="flex gap-4">
                <Linkedin size={20} style={{ color: "var(--accent)" }} className="mt-0.5 shrink-0" />
                <a href={settings.social.linkedin} target="_blank" rel="noopener noreferrer" className="text-sm" style={{ color: "var(--muted)" }}>linkedin.com/company/acharyaholdings</a>
              </div>
            )}
            {/* map placeholder */}
            {/* interactive map */}
            {settings?.mapEmbedUrl && (
              <div data-testid="contact-map" className="overflow-hidden rounded-[14px] border" style={{ borderColor: "var(--card-border)", background: "var(--bg-elev-1)" }}>
                <iframe
                  title="Acharya Holdings location"
                  src={settings.mapEmbedUrl}
                  className="block h-64 w-full"
                  style={{ border: 0, filter: "grayscale(0.15) contrast(1.02)" }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
                <a href={settings.mapLink || "https://maps.app.goo.gl/H2FYEAZj6pm3s8Gz5"} target="_blank" rel="noopener noreferrer"
                  data-testid="contact-map-directions"
                  className="flex items-center justify-center gap-2 border-t py-2.5 text-xs transition-colors"
                  style={{ borderColor: "var(--card-border)", color: "var(--muted)" }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "var(--accent)")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "var(--muted)")}>
                  <MapPin size={13} /> Open in Google Maps
                </a>
              </div>
            )}
          </div>

          {/* form */}
          <div className="rounded-[16px] border p-6 sm:p-8" style={{ background: "var(--surface)", borderColor: "var(--card-border)" }}>
            <form data-testid="contact-form" onSubmit={onSubmit} noValidate className="space-y-5">
              <div>
                <label className="mb-1.5 block text-xs" style={{ color: "var(--muted)" }}>Enquiry Type</label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger data-testid="contact-category-select" style={inputStyle}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-xs" style={{ color: "var(--muted)" }}>Name</label>
                  <input data-testid="contact-name-input" value={form.name} onChange={(e) => set("name", e.target.value)}
                    className="w-full rounded-[10px] border px-3.5 py-2.5 text-sm outline-none" style={inputStyle} />
                  {errors.name && <p data-testid="contact-form-error" className="mt-1 text-xs" style={{ color: "var(--danger)" }}>{errors.name}</p>}
                </div>
                <div>
                  <label className="mb-1.5 block text-xs" style={{ color: "var(--muted)" }}>Email</label>
                  <input data-testid="contact-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                    className="w-full rounded-[10px] border px-3.5 py-2.5 text-sm outline-none" style={inputStyle} />
                  {errors.email && <p className="mt-1 text-xs" style={{ color: "var(--danger)" }}>{errors.email}</p>}
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-xs" style={{ color: "var(--muted)" }}>Organisation (optional)</label>
                <input data-testid="contact-org-input" value={form.organisation} onChange={(e) => set("organisation", e.target.value)}
                  className="w-full rounded-[10px] border px-3.5 py-2.5 text-sm outline-none" style={inputStyle} />
              </div>
              <div>
                <label className="mb-1.5 block text-xs" style={{ color: "var(--muted)" }}>Message</label>
                <textarea data-testid="contact-message-input" rows={5} value={form.message} onChange={(e) => set("message", e.target.value)}
                  className="w-full rounded-[10px] border px-3.5 py-2.5 text-sm outline-none" style={inputStyle} />
                {errors.message && <p className="mt-1 text-xs" style={{ color: "var(--danger)" }}>{errors.message}</p>}
              </div>
              <button type="submit" disabled={submitting} data-testid="contact-form-submit-button"
                className="btn btn-primary w-full sm:w-auto">
                {submitting && <Loader2 size={15} className="animate-spin" />} Send Enquiry
              </button>
            </form>
          </div>
        </div>
      </section>
      </div>
    </>
  );
}
